import { forceArray } from '~/common/arrayUtils'
import { FetchOptions } from '~/common/fetchHelpers'

import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query'
import { queryClient } from '~/common/react-query/queryClient'
import { OffsetLimit, OffsetLimitPaginationResponse } from '~/api/common'
import qs from 'qs'
import { SANA_REBRAND_ENABLED } from '~/common/feature_flags'
import { Log } from '~/settings/settingsApi'
import fetch from '../common/fetch'

const FF_KEY = 'feature-flags'

type FlagResult = {
  name: string
  is_active: boolean
}

export const featureFlagsApi = {
  getFlagsDetails: async () => fetch.get(`/api/v2/feature-flag-admin/get-flags/`),
  updateFeatureFlag: async ({ flag, value }: { flag: string; value: boolean }): Promise<void> =>
    fetch.post('/api/v2/feature-flag-admin/update-flag/', { flag, is_active: value }),
  getActivity: (params: Partial<OffsetLimit>): Promise<OffsetLimitPaginationResponse<Log>> =>
    fetch.get(`/api/v2/feature-flag-admin/activity/${qs.stringify(params, { addQueryPrefix: true })}`),
  getAllFlags: async (): Promise<Record<string, boolean>> =>
    fetch
      .get('/api/v2/feature-flag/')
      .then((data: FlagResult[]) => Object.fromEntries(data.map(({ name, is_active }) => [name, is_active])))
}

export const useFeatureFlagsDetails = () =>
  useQuery({ queryKey: [FF_KEY, 'settings-details'], queryFn: featureFlagsApi.getFlagsDetails })

export const useFeatureFlagMutation = (
  useMutationOptions: UseMutationOptions<void, unknown, { flag: string; value: boolean }> = {}
) => {
  return useMutation({
    mutationFn: featureFlagsApi.updateFeatureFlag,
    ...useMutationOptions,
    onSettled(...args) {
      queryClient.invalidateQueries([FF_KEY])
      // The activity log is staying in the settings namespace for history preservation, at least for now
      queryClient.invalidateQueries(['settings', 'vndly_config', 'feature_flags', 'activity-log'])
      if (typeof useMutationOptions.onSettled === 'function') useMutationOptions.onSettled(...args)
    }
  })
}

export const useFeatureFlagActivityQuery = (filters?: Partial<OffsetLimit>) => {
  return useInfiniteQuery({
    queryKey: [FF_KEY, 'activity-log'],
    queryFn: ({ pageParam = 0 }) => {
      return featureFlagsApi.getActivity({ offset: pageParam, ...filters })
    },
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })
}

export const useFeatureFlags = (enabled?: boolean) =>
  useQuery({
    queryKey: [FF_KEY, 'values-all'],
    queryFn: featureFlagsApi.getAllFlags,
    enabled
  })

/**
 * @deprecated - old flag api
 */
export type FeatureResult<T> =
  // Single strings return booleans
  T extends string
    ? boolean
    : // String arrays return objects of keys to booleans
    T extends string[]
    ? Record<string, boolean>
    : // `as const` string arrays return concrete objects mapped to booleans
    T extends readonly string[]
    ? { [K in T[number]]: boolean }
    : never

/**
 * @deprecated - old flag api
 */
function getFeatureFlags<T extends string | string[] | readonly string[]>(
  features: T,
  fetchOptions?: FetchOptions
): Promise<FeatureResult<T>> {
  const featuresString = forceArray(features).sort().join(',')
  return fetch.get(`/api/v2/client/feature-flags/${featuresString}`, fetchOptions)
}

/**
 * @deprecated - old flag api
 */
export const featureFlagApi = {
  get: getFeatureFlags
}

/** Allow-listed path; must stay in sync with `PUBLIC_CLIENT_FEATURE_FLAGS` in `customers.api`. */
export function getPublicFeatureFlag(flag: typeof SANA_REBRAND_ENABLED): Promise<boolean> {
  return fetch.get(`/api/v2/client/public-feature-flags/${flag}/`)
}
